import React, { useState } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, TextField, Button } from '@mui/material';

import syncFindingsService from '../../services/syncFindingsService';
import AdminUtils from '../../services/utils';
import { StatusChip, SourceChip } from './statusBadge';

const IGNORE_REASONS = ['Known limitation', 'Test/staging account', 'Not a real issue', 'Other'];

function parseJsonColumn(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value; // e.g. the literal string "manual"
  }
}

function formatValue(value) {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function WouldChangeList({ wouldChange }) {
  if (wouldChange === 'manual' || !wouldChange) {
    return (
      <Typography variant="body2" sx={{ color: '#6B6578' }}>
        None &mdash; always requires manual review, never auto-corrected.
      </Typography>
    );
  }
  if (!Array.isArray(wouldChange) || wouldChange.length === 0) {
    return <Typography variant="body2" sx={{ color: '#6B6578' }}>None</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {wouldChange.map((change, i) => (
        <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '13px' }}>
          {change.field}: {formatValue(change.from)} &rarr; {formatValue(change.to)}
        </Typography>
      ))}
    </Box>
  );
}

function MetaRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: '#6B6578', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '11px' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#1F1B2E' }}>
        {value ?? <span style={{ color: '#9CA3AF' }}>&mdash;</span>}
      </Typography>
    </Box>
  );
}

function FindingDetail({ finding, onClose, onUpdated }) {
  const [resolutionNote, setResolutionNote] = useState(finding.resolution_note || '');
  const [ignoreReason, setIgnoreReason] = useState(finding.ignore_reason || IGNORE_REASONS[0]);
  const [showIgnoreReason, setShowIgnoreReason] = useState(false);
  const [showEndSubscriptionConfirm, setShowEndSubscriptionConfirm] = useState(false);
  const [endSubscriptionError, setEndSubscriptionError] = useState(null);
  const [saving, setSaving] = useState(false);

  const applyStatus = async (status, extra = {}) => {
    setSaving(true);
    try {
      await syncFindingsService.updateStatus(finding.id, { status, resolutionNote, ...extra });
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const handleEndSubscription = async () => {
    setSaving(true);
    setEndSubscriptionError(null);
    try {
      const result = await syncFindingsService.endSubscription(finding.id, { resolutionNote });
      if (result.ok) {
        onUpdated();
      } else {
        setEndSubscriptionError(result.error);
      }
    } finally {
      setSaving(false);
    }
  };

  const isOpen = finding.status === 'open';

  return (
    <Box sx={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 640 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <SourceChip source={finding.source} />
            <StatusChip finding={finding} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {finding.kind}
          </Typography>
        </Box>
        <Button onClick={onClose}>Close</Button>
      </Box>

      <Box
        sx={{
          background: '#FEF3C7',
          border: '1px solid #FCE3A1',
          borderRadius: '8px',
          padding: '12px 16px',
          fontSize: '14px',
          color: '#44404E',
        }}
      >
        {finding.detail}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
        <MetaRow
          label="User"
          value={finding.user_id ? `${finding.user_email || 'email unknown'} (#${finding.user_id})` : 'No local user'}
        />
        <MetaRow label="First Seen" value={AdminUtils.FormatDateWithTime(finding.detected_at)} />
        <MetaRow
          label="Last Seen"
          value={`${AdminUtils.FormatDateWithTime(finding.last_seen_at || finding.detected_at)} (seen ${finding.occurrence_count || 1}x)`}
        />
        <MetaRow label="Local ID" value={finding.local_id} />
        <MetaRow label="External ID" value={finding.external_id} />
        <MetaRow label="ShotKraft Status" value={finding.local_status} />
        <MetaRow label="Remote Status" value={finding.remote_status} />
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, marginBottom: 1 }}>
          Would change
        </Typography>
        <WouldChangeList wouldChange={parseJsonColumn(finding.would_change)} />
      </Box>

      {finding.status !== 'open' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#6B6578' }}>
            {finding.status === 'ignored' ? `Ignored` : 'Resolved'} by {finding.resolved_by || 'unknown'} on{' '}
            {finding.resolved_at ? AdminUtils.FormatDateWithTime(finding.resolved_at) : 'unknown date'}
            {finding.ignore_reason ? ` — reason: ${finding.ignore_reason}` : ''}
          </Typography>
        </Box>
      )}

      <TextField
        label="Note"
        placeholder="Add a note (optional)"
        multiline
        minRows={2}
        value={resolutionNote}
        onChange={(e) => setResolutionNote(e.target.value)}
      />

      {showIgnoreReason && (
        <FormControl size="small">
          <InputLabel id="ignore-reason-label">Ignore reason</InputLabel>
          <Select
            label="Ignore reason"
            labelId="ignore-reason-label"
            value={ignoreReason}
            onChange={(e) => setIgnoreReason(e.target.value)}
          >
            {IGNORE_REASONS.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showEndSubscriptionConfirm && (
        <Box sx={{ background: '#FDE2E7', border: '1px solid #F8C7D2', borderRadius: '8px', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#44404E' }}>
            This immediately sets local subscription #{finding.local_id} to <strong>canceled</strong> with
            today's end date. It does not call Stripe (there's nothing there to cancel) and does not delete
            the row &mdash; the user loses pro access right away. This also resolves this finding.
          </Typography>
          {endSubscriptionError && (
            <Typography variant="body2" sx={{ color: '#9F1239' }}>
              {endSubscriptionError}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {isOpen && !showIgnoreReason && !showEndSubscriptionConfirm && (
          <>
            <Button variant="contained" disabled={saving} onClick={() => applyStatus('resolved')}>
              Mark Resolved
            </Button>
            <Button variant="outlined" disabled={saving} onClick={() => setShowIgnoreReason(true)}>
              Ignore
            </Button>
            {finding.kind === 'sub_missing_in_stripe' && (
              <Button
                variant="outlined"
                color="error"
                disabled={saving}
                onClick={() => setShowEndSubscriptionConfirm(true)}
              >
                End Subscription
              </Button>
            )}
          </>
        )}
        {isOpen && showEndSubscriptionConfirm && (
          <>
            <Button variant="contained" color="error" disabled={saving} onClick={handleEndSubscription}>
              Confirm End Subscription
            </Button>
            <Button disabled={saving} onClick={() => { setShowEndSubscriptionConfirm(false); setEndSubscriptionError(null); }}>
              Cancel
            </Button>
          </>
        )}
        {isOpen && showIgnoreReason && (
          <>
            <Button variant="contained" disabled={saving} onClick={() => applyStatus('ignored', { ignoreReason })}>
              Confirm Ignore
            </Button>
            <Button disabled={saving} onClick={() => setShowIgnoreReason(false)}>
              Cancel
            </Button>
          </>
        )}
        {!isOpen && (
          <Button variant="outlined" disabled={saving} onClick={() => applyStatus('open')}>
            Reopen
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default FindingDetail;
